import { Editor, Path, Element as SlateElement, Transforms } from 'slate';

// ============================================================================
// Table Detection Utilities
// ============================================================================

/**
 * Check if the current selection is inside a table
 */
export const isInTable = (editor) => {
  const { selection } = editor;
  if (!selection) {
    return false;
  }

  const [tableNode] = Editor.nodes(editor, {
    match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'table',
  });

  return Boolean(tableNode);
};

/**
 * Get the current table entry [node, path] if selection is in a table
 */
export const getTableEntry = (editor) => {
  const { selection } = editor;
  if (!selection) {
    return null;
  }

  const [tableEntry] = Editor.nodes(editor, {
    match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'table',
  });

  return tableEntry || null;
};

/**
 * Get the current cell entry [node, path] if selection is in a table cell
 */
export const getCellEntry = (editor) => {
  const { selection } = editor;
  if (!selection) {
    return null;
  }

  const [cellEntry] = Editor.nodes(editor, {
    match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'table-cell',
  });

  return cellEntry || null;
};

/**
 * Get the current row entry [node, path] if selection is in a table row
 */
export const getRowEntry = (editor) => {
  const { selection } = editor;
  if (!selection) {
    return null;
  }

  const [rowEntry] = Editor.nodes(editor, {
    match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'table-row',
  });

  return rowEntry || null;
};

// ============================================================================
// Table Creation
// ============================================================================

/**
 * Create an empty cell node
 */
const createCell = () => ({
  type: 'table-cell',
  children: [{ type: 'paragraph', children: [{ text: '' }] }],
});

/**
 * Create a row with the specified number of cells
 */
const createRow = (columns) => ({
  type: 'table-row',
  children: Array.from({ length: columns }, () => createCell()),
});

/**
 * Create a table with the specified dimensions
 */
const createTable = (rows, columns) => ({
  type: 'table',
  children: Array.from({ length: rows }, () => createRow(columns)),
});

/**
 * Insert a new table at the current selection
 */
export const insertTable = (editor, rows = 2, columns = 2) => {
  if (isInTable(editor)) {
    // Don't insert table inside table
    return;
  }

  const table = createTable(rows, columns);
  Transforms.insertNodes(editor, table);

  // Move cursor to first cell
  const { selection } = editor;
  if (selection) {
    const [tableEntry] = Editor.nodes(editor, {
      match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'table',
      at: selection,
    });

    if (tableEntry) {
      const [, tablePath] = tableEntry;
      // Path to first cell: table -> first row -> first cell -> paragraph
      const firstCellPath = [...tablePath, 0, 0, 0];
      Transforms.select(editor, Editor.start(editor, firstCellPath));
    }
  }
};

// ============================================================================
// Row Operations
// ============================================================================

/**
 * Insert a row above the current row
 */
export const insertRowAbove = (editor) => {
  const rowEntry = getRowEntry(editor);
  if (!rowEntry) {
    return;
  }

  const [rowNode, rowPath] = rowEntry;
  const columnCount = rowNode.children.length;
  const newRow = createRow(columnCount);

  Transforms.insertNodes(editor, newRow, { at: rowPath });
};

/**
 * Insert a row below the current row
 */
export const insertRowBelow = (editor) => {
  const rowEntry = getRowEntry(editor);
  if (!rowEntry) {
    return;
  }

  const [rowNode, rowPath] = rowEntry;
  const columnCount = rowNode.children.length;
  const newRow = createRow(columnCount);

  Transforms.insertNodes(editor, newRow, { at: Path.next(rowPath) });
};

/**
 * Delete the current row
 */
export const deleteRow = (editor) => {
  const tableEntry = getTableEntry(editor);
  const rowEntry = getRowEntry(editor);
  if (!tableEntry || !rowEntry) {
    return;
  }

  const [tableNode] = tableEntry;
  const [, rowPath] = rowEntry;

  // If this is the only row, delete the entire table
  if (tableNode.children.length <= 1) {
    deleteTable(editor);
    return;
  }

  Transforms.removeNodes(editor, { at: rowPath });
};

// ============================================================================
// Column Operations
// ============================================================================

/**
 * Get the column index of the current cell
 */
const getColumnIndex = (editor) => {
  const cellEntry = getCellEntry(editor);
  if (!cellEntry) {
    return -1;
  }

  const [, cellPath] = cellEntry;
  return cellPath[cellPath.length - 1];
};

/**
 * Insert a column to the left of the current column
 */
export const insertColumnLeft = (editor) => {
  const tableEntry = getTableEntry(editor);
  if (!tableEntry) {
    return;
  }

  const columnIndex = getColumnIndex(editor);
  if (columnIndex < 0) {
    return;
  }

  const [tableNode, tablePath] = tableEntry;

  // Insert a cell in each row at the column index
  tableNode.children.forEach((_, rowIndex) => {
    const cellPath = [...tablePath, rowIndex, columnIndex];
    Transforms.insertNodes(editor, createCell(), { at: cellPath });
  });
};

/**
 * Insert a column to the right of the current column
 */
export const insertColumnRight = (editor) => {
  const tableEntry = getTableEntry(editor);
  if (!tableEntry) {
    return;
  }

  const columnIndex = getColumnIndex(editor);
  if (columnIndex < 0) {
    return;
  }

  const [tableNode, tablePath] = tableEntry;

  // Insert a cell in each row after the column index
  tableNode.children.forEach((_, rowIndex) => {
    const cellPath = [...tablePath, rowIndex, columnIndex + 1];
    Transforms.insertNodes(editor, createCell(), { at: cellPath });
  });
};

/**
 * Delete the current column
 */
export const deleteColumn = (editor) => {
  const tableEntry = getTableEntry(editor);
  if (!tableEntry) {
    return;
  }

  const columnIndex = getColumnIndex(editor);
  if (columnIndex < 0) {
    return;
  }

  const [tableNode, tablePath] = tableEntry;
  const columnCount = tableNode.children[0]?.children.length || 0;

  // If this is the only column, delete the entire table
  if (columnCount <= 1) {
    deleteTable(editor);
    return;
  }

  // Remove the cell at columnIndex from each row (in reverse to maintain paths)
  for (let rowIndex = tableNode.children.length - 1; rowIndex >= 0; rowIndex--) {
    const cellPath = [...tablePath, rowIndex, columnIndex];
    Transforms.removeNodes(editor, { at: cellPath });
  }
};

// ============================================================================
// Table Operations
// ============================================================================

/**
 * Delete the entire table
 */
export const deleteTable = (editor) => {
  const tableEntry = getTableEntry(editor);
  if (!tableEntry) {
    return;
  }

  const [, tablePath] = tableEntry;
  Transforms.removeNodes(editor, { at: tablePath });
};

// ============================================================================
// Keyboard Navigation
// ============================================================================

/**
 * Move to the next cell (Tab key behavior)
 * Returns true if handled, false otherwise
 */
export const moveToNextCell = (editor) => {
  const cellEntry = getCellEntry(editor);
  const rowEntry = getRowEntry(editor);
  const tableEntry = getTableEntry(editor);

  if (!cellEntry || !rowEntry || !tableEntry) {
    return false;
  }

  const [, cellPath] = cellEntry;
  const [rowNode, rowPath] = rowEntry;
  const [tableNode, tablePath] = tableEntry;

  const cellIndex = cellPath[cellPath.length - 1];
  const rowIndex = rowPath[rowPath.length - 1];

  // Try to move to next cell in same row
  if (cellIndex < rowNode.children.length - 1) {
    const nextCellPath = [...tablePath, rowIndex, cellIndex + 1, 0];
    Transforms.select(editor, Editor.start(editor, nextCellPath));
    return true;
  }

  // Try to move to first cell of next row
  if (rowIndex < tableNode.children.length - 1) {
    const nextCellPath = [...tablePath, rowIndex + 1, 0, 0];
    Transforms.select(editor, Editor.start(editor, nextCellPath));
    return true;
  }

  // At last cell of last row - insert new row
  insertRowBelow(editor);
  const newRowPath = [...tablePath, rowIndex + 1, 0, 0];
  Transforms.select(editor, Editor.start(editor, newRowPath));
  return true;
};

/**
 * Move to the previous cell (Shift+Tab key behavior)
 * Returns true if handled, false otherwise
 */
export const moveToPreviousCell = (editor) => {
  const cellEntry = getCellEntry(editor);
  const rowEntry = getRowEntry(editor);
  const tableEntry = getTableEntry(editor);

  if (!cellEntry || !rowEntry || !tableEntry) {
    return false;
  }

  const [, cellPath] = cellEntry;
  const [, rowPath] = rowEntry;
  const [tableNode, tablePath] = tableEntry;

  const cellIndex = cellPath[cellPath.length - 1];
  const rowIndex = rowPath[rowPath.length - 1];

  // Try to move to previous cell in same row
  if (cellIndex > 0) {
    const prevCellPath = [...tablePath, rowIndex, cellIndex - 1, 0];
    Transforms.select(editor, Editor.end(editor, prevCellPath));
    return true;
  }

  // Try to move to last cell of previous row
  if (rowIndex > 0) {
    const prevRowColumnCount = tableNode.children[rowIndex - 1].children.length;
    const prevCellPath = [...tablePath, rowIndex - 1, prevRowColumnCount - 1, 0];
    Transforms.select(editor, Editor.end(editor, prevCellPath));
    return true;
  }

  // At first cell - do nothing
  return true;
};

// ============================================================================
// Row Reordering
// ============================================================================

/**
 * Move a row from one index to another
 */
export const moveRow = (editor, fromIndex, toIndex) => {
  const tableEntry = getTableEntry(editor);
  if (!tableEntry) {
    return;
  }

  const [tableNode, tablePath] = tableEntry;

  if (fromIndex < 0 || fromIndex >= tableNode.children.length) {
    return;
  }
  if (toIndex < 0 || toIndex >= tableNode.children.length) {
    return;
  }
  if (fromIndex === toIndex) {
    return;
  }

  const fromPath = [...tablePath, fromIndex];
  const toPath = [...tablePath, toIndex];

  // Move the row
  Transforms.moveNodes(editor, {
    at: fromPath,
    to: toPath,
  });
};

// ============================================================================
// Column Width Management
// ============================================================================

/**
 * Update column widths for a table
 */
export const setColumnWidths = (editor, tablePath, columnWidths) => {
  Transforms.setNodes(editor, { columnWidths }, { at: tablePath });
};

/**
 * Get the column widths from a table node, or calculate defaults
 */
export const getColumnWidths = (tableNode, tableWidth) => {
  if (tableNode.columnWidths && tableNode.columnWidths.length > 0) {
    return tableNode.columnWidths;
  }

  // Calculate default equal widths
  const columnCount = tableNode.children[0]?.children.length || 1;
  const defaultWidth = Math.floor(tableWidth / columnCount);
  return Array.from({ length: columnCount }, () => defaultWidth);
};

// ============================================================================
// Row Reordering (via context menu)
// ============================================================================

/**
 * Move the current row up
 */
export const moveRowUp = (editor) => {
  const tableEntry = getTableEntry(editor);
  const rowEntry = getRowEntry(editor);
  if (!tableEntry || !rowEntry) {
    return;
  }

  const [, tablePath] = tableEntry;
  const [, rowPath] = rowEntry;
  const rowIndex = rowPath[rowPath.length - 1];

  // Can't move up if already at top
  if (rowIndex <= 0) {
    return;
  }

  const fromPath = [...tablePath, rowIndex];
  const toPath = [...tablePath, rowIndex - 1];

  Transforms.moveNodes(editor, {
    at: fromPath,
    to: toPath,
  });
};

/**
 * Move the current row down
 */
export const moveRowDown = (editor) => {
  const tableEntry = getTableEntry(editor);
  const rowEntry = getRowEntry(editor);
  if (!tableEntry || !rowEntry) {
    return;
  }

  const [tableNode, tablePath] = tableEntry;
  const [, rowPath] = rowEntry;
  const rowIndex = rowPath[rowPath.length - 1];

  // Can't move down if already at bottom
  if (rowIndex >= tableNode.children.length - 1) {
    return;
  }

  const fromPath = [...tablePath, rowIndex];
  const toPath = [...tablePath, rowIndex + 2]; // +2 because Slate move is "before" target

  Transforms.moveNodes(editor, {
    at: fromPath,
    to: toPath,
  });
};

// ============================================================================
// Backspace Handling
// ============================================================================

/**
 * Handle backspace in table cells - prevent deleting cell structure
 * Returns true if backspace was handled (should prevent default)
 */
export const handleBackspaceInTable = (editor) => {
  if (!isInTable(editor)) {
    return false;
  }

  const { selection } = editor;
  if (!selection) {
    return false;
  }

  // Only handle when cursor is at the start of a cell
  const cellEntry = getCellEntry(editor);
  if (!cellEntry) {
    return false;
  }

  const [, cellPath] = cellEntry;

  // If cursor is at the very start of the cell, prevent backspace
  if (Editor.isStart(editor, selection.anchor, cellPath)) {
    return true; // Prevent default backspace behavior
  }

  return false;
};

// ============================================================================
// Enter Handling
// ============================================================================

/**
 * Check if we're at the very end of the last cell in the table.
 * Returns null if not at end, or an object with tablePath and rowIndex if at end.
 */
const getEndOfTableInfo = (editor) => {
  const cellEntry = getCellEntry(editor);
  const rowEntry = getRowEntry(editor);
  const tableEntry = getTableEntry(editor);

  if (!cellEntry || !rowEntry || !tableEntry) {
    return null;
  }

  const { selection } = editor;
  if (!selection) {
    return null;
  }

  const [, cellPath] = cellEntry;
  const [rowNode, rowPath] = rowEntry;
  const [tableNode, tablePath] = tableEntry;

  const cellIndex = cellPath[cellPath.length - 1];
  const rowIndex = rowPath[rowPath.length - 1];

  // Check if this is the last cell of the last row
  const isLastRow = rowIndex === tableNode.children.length - 1;
  const isLastCell = cellIndex === rowNode.children.length - 1;

  if (!isLastRow || !isLastCell) {
    return null;
  }

  // Check if cursor is at the end of the cell
  if (!Editor.isEnd(editor, selection.anchor, cellPath)) {
    return null;
  }

  return { tablePath, rowIndex };
};

/**
 * Handle Enter in table cells
 * Returns true if Enter was handled (should prevent default)
 *
 * Behavior:
 * - If at the end of the last cell in the table, add a new row
 * - Otherwise, let Slate handle it normally (insert line break in cell)
 */
export const handleEnterInTable = (editor) => {
  if (!isInTable(editor)) {
    return false;
  }

  // If at the end of the last cell, add a new row (like Tab behavior)
  const endInfo = getEndOfTableInfo(editor);
  if (endInfo) {
    const { tablePath, rowIndex } = endInfo;

    // Insert a new row below
    insertRowBelow(editor);

    // Move cursor to the first cell of the new row
    const newRowPath = [...tablePath, rowIndex + 1, 0, 0];
    Transforms.select(editor, Editor.start(editor, newRowPath));

    return true;
  }

  return false;
};
